/**
 * /pass/guia/:slug — Public guide page.
 * Free recommendation content (no commission). Includes Google Maps embed if
 * coordinates or address are present.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, ArrowLeft, MapPin, Navigation, Sparkles } from 'lucide-react';
import { passPublic } from '../../services/passApi';
import { renderMarkdown } from '../../utils/simpleMarkdown';

const CATEGORY_LABEL = {
  general: 'General', playa: 'Playa', mirador: 'Mirador', mercado: 'Mercado',
  museo: 'Museo', parque: 'Parque', monumento: 'Monumento', evento: 'Evento',
  gastronomia: 'Gastronomía', ruta: 'Ruta', transporte: 'Transporte',
};

export default function GuideView() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    passPublic.guide(slug)
      .then((res) => alive && setGuide(res.data.guide))
      .catch((err) => alive && setError(err.response?.data?.detail || 'Guía no encontrada'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando...</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-orange-500 mb-3" />
          <p className="text-slate-600">{error}</p>
          <Link to="/pass" className="text-teal-600 underline mt-3 inline-block">Volver</Link>
        </div>
      </div>
    );
  }

  const hasCoords = guide.latitude != null && guide.longitude != null;
  const mapQuery = hasCoords
    ? `${guide.latitude},${guide.longitude}`
    : guide.address;
  const mapsEmbedSrc = mapQuery
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&output=embed`
    : null;
  const directionsUrl = mapQuery
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery)}`
    : null;

  const bodyHtml = renderMarkdown(guide.body);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{guide.title} — Todo en Local Pass</title>
        {guide.summary && <meta name="description" content={guide.summary} />}
      </Helmet>

      {/* Cover */}
      <div className="relative bg-slate-900 text-white">
        {guide.cover_image_url && (
          <img
            src={guide.cover_image_url}
            alt={guide.title}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
        )}
        <div className="relative max-w-3xl mx-auto px-6 py-12 sm:py-16">
          <Link to={-1} className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-3">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-500/20 text-teal-200 text-[11px] font-semibold mb-2">
            <Sparkles className="w-3 h-3" /> Recomendación local — Pass
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">{guide.title}</h1>
          {guide.summary && <p className="text-slate-200 max-w-2xl">{guide.summary}</p>}
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/80">
              {CATEGORY_LABEL[guide.category] || guide.category}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white/80">
              <MapPin className="w-3 h-3" /> {guide.city}
            </span>
            {(guide.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/80">#{t}</span>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {bodyHtml ? (
          <article
            className="prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        ) : (
          <p className="text-slate-500 italic">Sin contenido todavía.</p>
        )}

        {mapsEmbedSrc && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-slate-900 mb-3 inline-flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-600" /> Cómo llegar
            </h2>
            {guide.address && <p className="text-sm text-slate-600 mb-3">{guide.address}</p>}
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Mapa"
                src={mapsEmbedSrc}
                width="100%"
                height="320"
                loading="lazy"
                style={{ border: 0 }}
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
            >
              <Navigation className="w-4 h-4" /> Abrir indicaciones en Google Maps
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
